/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import EntryAndExitFormDetailsSectionInfo
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/EntryAndExitFormDetailsSectionInfo";
import {permissionObj} from "../../../../../../../Helpers/permission";
import routes from "../../../../../../../Routes/routes";
import DivContainerBody from "../../../../../../../components/DivContainerBody";
import HeadTittle from "../../../../../../../components/HeadTittle";
import CustomChip from "../../../../../../../components/CustomChip";
import {colors} from "../../../../../../../enums/colors";
import {EditIcon, IconDocumentWarning} from "../../../../../../../components/buttons/IconButtons";
import ButtonAction from "../../../../../../../components/buttons/ButtonAction";
import ReportDispatchModal from "../Modal/ReportDispatchModal";
import StatusBadgeSection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/StatusBadgeSection";
import DiscrepancySection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/DiscrepancySection";
import GuardGateLogSection from "../Sections/GuardGateLogSection";
import GuardDispatchGateLogSection from "../Sections/GuardDispatchGateLogSection";
import GuardTransportSection from "../Sections/GuardTransportSection";
import {
    getGateLogNotePlaceholder,
    MATERIAL_CONTROL_REGISTERED_GATE_STATUS,
    showsGuardGateMovementLog,
    showsGuardTransport,
    showsGuardTransportRecord,
    showsGuardTripRecord,
    wasGuardReportedOnVehicleMovement
} from "../Model/Model";
import {wasMaterialControlDispatched} from "../../../../../MaterialControl/Model/Model";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";

/**
 * Detail of a plant material control form read by the guard at the gate.
 *
 * The information of the folio is the one the requester registered, so the
 * guard only reads it and completes the gate log of the movement: the notes of
 * what arrives at the plant and the evidence that supports it. A movement
 * reported with discrepancies displays them above the log, because it has to
 * be reviewed before the materials can be dispatched.
 *
 * @param {Object} props - Information of the folio and handlers of the gate log.
 * @param {Function} props.t - Translation handler.
 * @param {Function} props.navigate - Navigation handler.
 * @param {boolean} props.DEV - Whether the development mode is active.
 * @param {Array} props.permission - Permissions of the user reading the folio.
 * @param {Object} props.contentCardRef - Reference of the content of the information card.
 * @param {Object} props.contentTableRef - Reference of the content of the gate log.
 * @param {Object} props.containerCardRef - Reference of the container of the information card.
 * @param {Object} props.containerTableRef - Reference of the container of the gate log.
 * @param {Object} props.item - Folio the movement belongs to.
 * @param {Array} props.evidence - Evidence registered by the requester.
 * @param {Object} props.form - Type of the form being displayed.
 * @param {boolean} props.showBadge - Whether the dispatch of the folio is displayed.
 * @param {Object} props.gateLog - Gate log captured by the guard.
 * @param {Function} props.setGateLog - State updater of the gate log.
 * @param {Function} props.handleChange - Change handler of the fields of the gate log.
 * @param {Object} props.photoPicker - Evidence of the movement captured at the gate.
 * @param {Function} props.setPhotoPicker - State updater of the evidence of the gate.
 * @param {boolean} props.emptyFields - Whether the required fields are marked.
 * @param {boolean} props.emptyFieldsItems - Whether the required fields of the materials are marked.
 * @param {Function} props.setEmptyFieldsItems - State updater of the marked fields of the materials.
 * @param {Function} props.setModified - Reads the folio again when a material is delivered.
 * @param {boolean} props.showConfirmModal - Whether the confirmation of the report is displayed.
 * @param {Function} props.handleDispatch - Registers the dispatch of the movement.
 * @param {Function} props.openReportModal - Opens the report of the discrepancies.
 * @param {Function} props.handleActionModal - Registers the confirmed report with its comment and evidence.
 * @param {Function} props.closeModal - Closes the confirmation of the report.
 * @param {Function} props.goBack - Returns to the list of forms.
 *
 * @returns {JSX.Element} Detail of the folio with the gate log of the guard.
 */
const GuardPlantMaterialControlDetails = ({
                                          t,
                                          navigate,
                                          DEV,
                                          isGuard,
                                          permission,
                                          contentCardRef,
                                          contentTableRef,
                                          containerCardRef,
                                          containerTableRef,
                                          item,
                                          evidence,
                                          form,
                                          showBadge,
                                          gateLog,
                                          setGateLog,
                                          handleChange,
                                          photoPicker,
                                          setPhotoPicker,
                                          emptyFields,
                                          emptyFieldsItems,
                                          setEmptyFieldsItems,
                                          setModified,
                                          showConfirmModal,
                                          handleDispatch,
                                          openReportModal,
                                          handleActionModal,
                                          closeModal,
                                          goBack
                                      }) => {

    if (!DEV && !isGuard &&
        !permission?.includes(permissionObj.WEB_ENTRY_AND_EXIT_FORMS_READ)
    )
        navigate(routes.forbidden);

    // The dispatch hands the material over and the departure takes it out of
    // the plant, a folio reported with discrepancies is still open, so the
    // actions that close it are read until one of those movements is
    // registered.
    const isClosed = MATERIAL_CONTROL_REGISTERED_GATE_STATUS.includes(item?.dispatchStatusId);

    const hasDiscrepancies = item?.dispatchStatusId === STATUS_MODEL.DISCREPANCY;

    // The report replaces the status the movement was registered with, so a
    // folio reported with discrepancies keeps reading the log of the gate, only
    // read, whether the guard already wrote it or was still waiting to.
    const showReportedLog = hasDiscrepancies;

    // A folio that already handed its materials over keeps reading the log the
    // gate wrote, whether it was closed by the dispatch or still waits for the
    // vehicle to leave the plant.
    const isDispatched = wasMaterialControlDispatched(item);

    // The gate writes nothing else on a folio that was reported or that already
    // dispatched its materials, so its log is read and no longer completed.
    const isLogDisabled = isClosed || hasDiscrepancies || isDispatched;

    // A folio reported with discrepancies has to be reviewed before the gate
    // writes anything else on it, so it is only read until the report is attended
    const canClose = (DEV || isGuard)
        && !isClosed
        && !hasDiscrepancies;

    // Once the arrival is registered the folio waits for its dispatch, so what
    // the requester left blank is the block the guard completes before closing
    // the movement.
    const isPendingDispatch = item?.dispatchStatusId === STATUS_MODEL.PENDING_TO_DISPATCH;

    // The gate writes the log of the movement the folio waits for, so the one
    // that only registers the vehicle reads nothing of the dispatch in its own
    // card: the movement of the materials is the one displayed in the record of
    // the folio, and it is not repeated where the guard already writes it.
    // The report replaces the status the folio was waiting in, so a folio
    // reported during a movement of the vehicle keeps reading the log of that
    // movement, and one reported while its dispatch was being completed keeps
    // reading that same form, with its materials, only read.
    const showsVehicleLog = showsGuardGateMovementLog(item)
        || wasGuardReportedOnVehicleMovement(item);

    // A folio reported with discrepancies is the one being reviewed, so the
    // gate reads the whole record of what happened to it: the movement of the
    // materials is displayed with the notes and the evidence the guard wrote
    // for it, and not only the report that closed it.
    // A dispatched folio keeps reading the movement of the materials only while
    // it required the arrival or the departure of the vehicle.
    const showTripRecord = showsVehicleLog
        || hasDiscrepancies
        || (item?.dispatchStatusId === STATUS_MODEL.DISPATCHED && showsGuardTripRecord(item));

    // The vehicle is read in the moment it is in front of the guard, so the
    // transport is captured while the folio waits for the arrival of the unit,
    // or with the dispatch when the folio goes straight to it
    const showTransport = showsGuardTransport(item);

    // The vehicle that was already written is read again on the step that
    // follows the one it was captured in, so the guard reads the unit the
    // movement travels with and no longer writes it
    const showTransportRecord = showsGuardTransportRecord(item);
    const isTransportDisabled = isClosed || hasDiscrepancies || !showTransport;

    // The log is written for the movement the folio waits for, so the notes ask
    // for what is seen when the vehicle comes in or for what is seen when it
    // leaves the plant
    const notePlaceholder = getGateLogNotePlaceholder({
        statusId: item?.dispatchStatusId,
        movementDirection: item?.movementDirection
    });

    return (
        <>
            <HeadTittle
                name={'details'}
                onBack={goBack}
                divStack={
                    <CustomChip legend={t(form.trans)}
                                background={colors.secondary100}
                                fontColor={colors.secondary700}
                                borderColor={colors.secondary150}
                                variant={'compact'}
                                icon={form.Icon}
                                classNameIcon={'icon-color-secondary-700 h-16px w-16px'}
                    />
                }
            >
                {canClose &&
                    <>
                        <ButtonAction
                            id='btn_gpmcd_dispatch'
                            className='btn-icon-edit'
                            Icon={EditIcon}
                            name='dispatch'
                            onClick={() => handleDispatch()}
                        />
                        <ButtonAction
                            id='btn_gpmcd_report'
                            className='btn-icon-delete'
                            Icon={IconDocumentWarning}
                            name='report'
                            onClick={() => openReportModal()}
                        />
                    </>
                }
            </HeadTittle>
            <DivContainerBody>
                <div className={"mb-4"}>
                    {/* The gate is the one that dispatched the folio, so it is
                        not told back that it was dispatched; the report it has
                        to attend is the only result read at the top. */}
                    <StatusBadgeSection
                        t={t}
                        item={item}
                        showBadge={showBadge && hasDiscrepancies}
                    />
                </div>
                <div className='row px-0'>
                    <div className='col-sm-12 col-md-4 col-lg-4 mb-lg-0 mb-md-3 mb-sm-3 pl-0 pr-sm-0 pr-md-3'
                         ref={containerCardRef}>
                        <EntryAndExitFormDetailsSectionInfo
                            item={item}
                            evidence={evidence}
                            form={form}
                            contentCardRef={contentCardRef}
                            showDepartureRecord={false}
                            showTripRecord={showTripRecord}
                        />
                    </div>
                    <div className='col-sm-12 col-md-8 col-lg-8 px-0' ref={containerTableRef}>
                        {/* The detail stacks the sections the guard closes the movement with, so
                            the column is the one that scrolls and every card is displayed whole. */}
                        <div className={"overflow-y-auto"} ref={contentTableRef}>
                            {hasDiscrepancies &&
                                <div className={"mb-3"}>
                                    <DiscrepancySection
                                        t={t}
                                        reviewedBy={item?.dispatchUser}
                                        reviewedAt={item?.dispatchDate}
                                        reason={item?.dispatchReason}
                                        files={item?.discrepancyFiles}
                                    />
                                </div>
                            }

                            {showsVehicleLog ?
                                <GuardGateLogSection
                                    t={t}
                                    gateLog={gateLog}
                                    handleChange={handleChange}
                                    photoPicker={photoPicker}
                                    setPhotoPicker={setPhotoPicker}
                                    emptyFields={emptyFields}
                                    disabled={isClosed || hasDiscrepancies}
                                    notePlaceholder={notePlaceholder}
                                    // The transport is completed while the
                                    // vehicle is at the gate, so the log of that
                                    // movement opens with the unit it was read
                                    // with
                                    transportSection={(showTransport || showTransportRecord) &&
                                        <GuardTransportSection
                                            t={t}
                                            item={item}
                                            gateLog={gateLog}
                                            handleChange={handleChange}
                                            emptyFields={emptyFields}
                                            disabled={isTransportDisabled}
                                        />
                                    }
                                />
                                : isPendingDispatch || isDispatched || isClosed || showReportedLog ?
                                    <GuardDispatchGateLogSection
                                        t={t}
                                        item={item}
                                        gateLog={gateLog}
                                        setGateLog={setGateLog}
                                        handleChange={handleChange}
                                        photoPicker={photoPicker}
                                        setPhotoPicker={setPhotoPicker}
                                        emptyFields={emptyFields}
                                        emptyFieldsItems={emptyFieldsItems}
                                        setEmptyFieldsItems={setEmptyFieldsItems}
                                        disabled={isLogDisabled}
                                        notePlaceholder={notePlaceholder}
                                        showTransport={showTransport || showTransportRecord}
                                        transportDisabled={!showTransport}
                                    />
                                    :
                                        <></>
                            }
                        </div>
                    </div>
                </div>
            </DivContainerBody>

            { showConfirmModal &&
                <ReportDispatchModal
                    show={showConfirmModal}
                    onHide={closeModal}
                    onConfirm={(comment, files) => handleActionModal(comment, files)}
                    title_modal={'report_dispatch'}
                    description={t('are_you_sure_to_proceed')}
                    subDescription={t('please_provide_the_required_information_below')}
                />
            }
        </>
    );
};

export default GuardPlantMaterialControlDetails;
